import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Contacts } from '@capacitor/contacts';
import { ContactRound } from 'lucide-react';
import { useIsPhone } from '../lib/useIsPhone.js';
import { contactFailure, nativeContactValues, webContactValues, type DeviceContact, type WebContact } from '../lib/deviceContact.js';
import { Button } from './ui.js';
import { useToast } from './Toast.js';

interface WebContactManager { select(properties: string[], options: { multiple: boolean }): Promise<WebContact[]> }
export function DeviceContactPicker({ onPick }: { onPick: (contact: DeviceContact) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const phone = useIsPhone();
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const native = Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Contacts');
  const web = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { contacts?: WebContactManager }).contacts;
  if (!native && !(phone && web && window.isSecureContext)) return null;
  const pick = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      if (native) {
        const contact = await Contacts.pickContact();
        if (mounted.current) onPick(nativeContactValues(contact));
      }
      else if (web) {
        const [contact] = await web.select(['name', 'tel', 'email'], { multiple: false });
        if (contact && mounted.current) onPick(webContactValues(contact));
      }
    } catch (error) {
      const reason = contactFailure(error);
      if (mounted.current && reason !== 'cancelled') toast.error(t(reason === 'denied' ? 'common.contactPermissionDenied' : 'common.contactUnavailable'));
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  return <Button type="button" variant="ghost" loading={busy} icon={<ContactRound className="h-4 w-4" />} onClick={() => void pick()}>{t('common.pickContact')}</Button>;
}
